import React from 'react';
import { motion } from 'framer-motion';
import { Timer, Activity } from 'lucide-react';

interface Exercise {
  name: string;
  duration: number;
  intensity: string;
  description?: string;
}

interface WorkoutBreakdownProps {
  exercises?: Exercise[];
}

export function WorkoutBreakdown({ exercises = [] }: WorkoutBreakdownProps) {
  const totalDuration = exercises.reduce((sum, ex) => sum + ex.duration, 0);

  if (!exercises.length) {
    return (
      <div className="space-y-4">
        <div className="flex items-center gap-2">
          <Timer className="w-5 h-5 text-primary" />
          <h2 className="font-semibold">Workout Breakdown</h2>
        </div>
        <div className="text-center text-muted-foreground py-8">
          No exercise breakdown available for this workout.
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-2">
        <Timer className="w-5 h-5 text-primary" />
        <h2 className="font-semibold">Workout Breakdown</h2>
      </div>

      <div className="space-y-3">
        {exercises.map((exercise, index) => (
          <motion.div
            key={index}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: index * 0.1 }}
            className="bg-muted/30 p-4 rounded-lg"
          >
            <div className="flex items-center justify-between mb-2">
              <div>
                <div className="font-medium">{exercise.name}</div>
                <div className="text-sm text-muted-foreground">{exercise.duration} seconds</div>
              </div>
              <div className="flex items-center gap-2">
                <Activity className="w-4 h-4 text-primary" />
                <span className="text-sm">{exercise.intensity}</span>
              </div>
            </div>

            {exercise.description && (
              <p className="text-sm text-muted-foreground">{exercise.description}</p>
            )}

            {/* Progress Bar */}
            <div className="mt-2 h-1 bg-muted rounded-full overflow-hidden">
              <div 
                className="h-full bg-primary"
                style={{ width: `${(exercise.duration / totalDuration) * 100}%` }}
              />
            </div>
          </motion.div>
        ))}
      </div>
    </div>
  );
}